import React, { useEffect, useState } from 'react';
import { useAppContext } from 'context/AppContext';
import PropTypes from 'prop-types';
import strings from 'localization/strings';
import arrayMutators from 'final-form-arrays';
import paymentProviders from 'enums/paymentProviders';
import purchaseTypes from 'enums/purchaseTypes';
import postInvoiceTypes from 'enums/postInvoiceTypes';
import { createPaymentFormInitialValues, sanitizePaymentFormValues, stripeFormIsIncomplete } from 'logic/paymentLogic';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Form from 'form/Form';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import StripeElementsWrapper from 'components/stripe/StripeElementsWrapper';
import PaymentElement from 'components/payment/PaymentElement';
import FullScreenLoader from 'common/FullScreenLoader';
import Amount from 'common/Amount';
import PurchasePaymentSpecification from './PurchasePaymentSpecification';

const LockSystemAdditionalUsersPaymentDialog = ({ booking, bookingItems, open, onOk, onCancel }) => {
    const { appContext } = useAppContext();
    const [initialValues, setInitialValues] = useState(undefined);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if(open && bookingItems) {
            setInitialValues({
                payment: createPaymentFormInitialValues({
                    paymentProvider: paymentProviders.stripe.key
                }, appContext)
            });
        }
    }, [open]);

    if(!bookingItems) {
        return null;
    }

    const createStripePaymentIntentRequest = {
        bookingId: booking.id,
        type: purchaseTypes.postInvoice.key,
        postInvoiceType: postInvoiceTypes.lockSystemAdditionalUserAddition.key,
        postInvoiceData: {
            bookingItems
        }
    };

    const handleFormSubmit = values => {
        setIsSaving(true);
        onOk({
            values,
            onCompleted: () => setIsSaving(false)
        });
    };

    return (
        <StripeElementsWrapper createStripePaymentIntentRequest={createStripePaymentIntentRequest}>
            {
                ({ stripePaymentContext }) => (
                    <Dialog open={open} onClose={onCancel} disableEnforceFocus /* to be able to set focus to elements outside the dialog, e.g. when authorizing a payment method in an iframe provided by Stripe */>
                        <FullScreenLoader open={isSaving} />
                        <Form
                            initialValues={initialValues}
                            mutators={arrayMutators}
                            onSubmit={values => handleFormSubmit(sanitizePaymentFormValues(values, stripePaymentContext))}
                        >
                            {({ values, handleSubmit, invalid }) => (
                                <form onSubmit={handleSubmit}>
                                    <DialogTitle disableTypography>
                                        <Typography variant="h5">
                                            {strings.manageLockSystemAdditionalUsers}
                                        </Typography>
                                    </DialogTitle>
                                    <DialogContent>
                                        <PaymentElement
                                            name="payment"
                                            paymentProviders={[paymentProviders.stripe.key]}
                                            stripePaymentContext={stripePaymentContext}
                                        >
                                            <PurchasePaymentSpecification booking={booking} purchaseItems={stripePaymentContext.purchase.purchaseItems} />
                                       </PaymentElement>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            disabled={invalid || stripeFormIsIncomplete(values.payment, stripePaymentContext)}
                                        >
                                            {strings.formatString(paymentProviders.stripe.payAmountLabel, <Amount value={stripePaymentContext.purchase.amount} currency={stripePaymentContext.purchase.currency}/>)}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={onCancel}
                                        >
                                            {strings.cancel}
                                        </Button>
                                    </DialogActions>
                                </form>
                            )}
                        </Form>
                    </Dialog>
                )
            }
        </StripeElementsWrapper>
    );
};

LockSystemAdditionalUsersPaymentDialog.propTypes = {
    booking: PropTypes.object.isRequired,
    bookingItems: PropTypes.array,
    open: PropTypes.bool.isRequired,
    onOk: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default LockSystemAdditionalUsersPaymentDialog;
